import React, { useEffect, useRef, useMemo, useState } from 'react'
import getProp from 'lodash/get';
import setProp from 'lodash/set';
import classNames from 'classnames';

import Section from './Section';
import Tabs from './Tabs';
import ErrorBoundary from './ErrorBoundary';
import Registry from './inputs/Registry';
import ValidatorRegistry from './validators/ValidatorRegistry';
import './inputs/Repeater';

const mapRefData = (originalValues, refData, schema) => {
	const sections = schema.sections || [schema];
	sections.forEach(section => {
		if (section.refDataMap) {
			Object.entries(section.refDataMap).forEach(([k, v]) => {
				setProp(originalValues, k, getProp(refData, v));
			});
		}
	});

	return originalValues;
}

const isBrowser = typeof window !== undefined;
const getFieldErrors = async ({ name: fieldName, validators = [], required }, values) => {
	let validatorsToCheck = [...validators];
	if (required) {
		validatorsToCheck.push({ name: 'required', errorMessage: 'This field is required' });
	}
	const value = getProp(values, fieldName);
	const resultingErrors = await Promise.all(
		validatorsToCheck.map(async ({ name: validatorName, func, errorMessage }) => {
			const validator = !!func ? func : ValidatorRegistry.getValidator(validatorName);
			if (!validator || await validator(value || '', values)) {
				return false;
			}

			return errorMessage;
		})
	);

	const errors = resultingErrors.filter(err => err);

	if (!fieldName || errors.length === 0) {
		return {};
	}
	return { [fieldName]: errors };
}

const getErrors = async (fields = [], values) => {
	const errorList = await Promise.all(
		fields.map(field => getFieldErrors(field, values))
	);

	const errors = errorList.reduce((errs, error) => Object.assign(errs, error), {});

	return { hasErrors: Object.keys(errors).length > 0, errors };
}

const disableFields = (fields) => {
	const disabledFieldsArr = fields.map(field => {
		if (field.fields) {
			const disabledFields = disableFields(field.fields);
			return { ...field, fields: disabledFields };
		}
		return { ...field, disabled: true };
	});
	return disabledFieldsArr;
}

const Form = ({
	schema = {},
	originalValues = {},
	refData = {},
	style = {},
	CustomNavigation = null,
	customNavigationColors = {},
	disabled = false,
	selectedLanguage = 'EN',
	currentSectionIndex = 0,
	onChange = () => { },
	commit = () => { },
	onNext = () => { },
	onPrevious = () => { },
	onSubmit = () => { },
	onFieldChange,
	onSectionNavigate,
}) => {
	const [values, setValues] = useState(() => mapRefData(originalValues, refData, schema));
	const [pendingActions, setPendingActions] = useState([]);
	const [currentSection, setCurrentSection] = useState(currentSectionIndex);
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(false);

	const sections = schema.sections || [schema];
	const rawSection = sections[currentSection] || {};
	const section = useMemo(() => {
		if (disabled && rawSection.fields) {
			return { ...rawSection, fields: disableFields(rawSection.fields) };
		}
		return rawSection;
	}, [disabled, rawSection]);

	const sectionsRef = useRef(sections);
	sectionsRef.current = sections;
	useEffect(() => {
		const s = sectionsRef.current[currentSection] || {};
		try { onSectionNavigate?.('started', currentSection, null, {
			sectionType: s.type,
			sectionName: s.name || s.id,
			totalSections: sectionsRef.current.length,
		}); } catch { /* no-op */ }
	}, [currentSection]);

	// Functional updates: add/remove must apply to the latest committed state, not the
	// `pendingActions` snapshot captured when these closures were created. Concurrent uploads
	// (e.g. a multi-document soc-sec slot uploading 3 files at once) each call removePendingAction
	// from the render in which their upload STARTED; with a value-form setState those stale
	// closures resurrect already-removed actions, leaving a residue that never drains and
	// permanently blocks next()/submit() at `if (pendingActions.length > 0) return`.
	const addPendingAction = (action) => {
		setPendingActions((prev) => [...prev, action]);
	}

	const removePendingAction = (action) => {
		setPendingActions((prev) => prev.filter(act => act !== action));
	}

	const onChangeValues = ({ target }) => {
		const newValues = setProp(Object.assign({}, values), target.name, target.value);
		try { onFieldChange?.(target.name, target.value, {
			sectionIndex: currentSection,
			sectionType: section.type,
			sectionName: section.name || section.id,
		}); } catch { /* no-op */ }
		onChange(newValues);
		setValues(newValues);
	};

	const commitValues = async ({ target }) => {
		const newValues = setProp(Object.assign({}, values), target.name, target.value);
		try { onFieldChange?.(target.name, target.value, {
			sectionIndex: currentSection,
			sectionType: section.type,
			sectionName: section.name || section.id,
		}); } catch { /* no-op */ }
		setValues(newValues);
		setLoading(true);
		await commit(newValues);
		setLoading(false);
	};

	const checkSectionComplete = () => {
		const requiredFieldsAreFilled = !(section.fields || []).find(f => f.required && !getProp(values, f.name));
		const sectionIsComplete = !section.needsValidation || (!!values[section.name] && !!values[section.name].isComplete);
		return requiredFieldsAreFilled && sectionIsComplete;
	}

	useEffect(() => {
		setCurrentSection(currentSectionIndex);
	}, [currentSectionIndex]);

	const next = async (event) => {
		event.preventDefault()
		if (pendingActions.length > 0) {
			return;
		}
		const { hasErrors, errors: sectionErrors } = await getErrors(section.fields, values);
		setErrors(sectionErrors);
		if (hasErrors) {
			try { onSectionNavigate?.('validation_failed', currentSection, null, {
				sectionType: section.type,
				sectionName: section.name || section.id,
				totalSections: sections.length,
				errorCount: Object.keys(sectionErrors).length,
				errorFields: Object.keys(sectionErrors),
			}); } catch { /* no-op */ }
			return;
		}
		if (currentSection === sections.length - 1) return submit(event);
		try { onSectionNavigate?.('next', currentSection, currentSection + 1, {
			sectionType: section.type,
			sectionName: section.name || section.id,
			totalSections: sections.length,
		}); } catch { /* no-op */ }
		if (isBrowser) window.scrollTo({ top: 0, behavior: 'smooth' });
		onNext();
		setCurrentSection(Math.min(currentSection + 1, sections.length - 1));
	};

	const submit = async (event) => {
		event.preventDefault();
		if (disabled) return;
		const { hasErrors, errors: sectionErrors } = await getErrors(section.fields, values);
		setErrors(sectionErrors)
		if (hasErrors) {
			try { onSectionNavigate?.('validation_failed', currentSection, null, {
				sectionType: section.type,
				sectionName: section.name || section.id,
				totalSections: sections.length,
				errorCount: Object.keys(sectionErrors).length,
				errorFields: Object.keys(sectionErrors),
			}); } catch { /* no-op */ }
			return;
		}
		try { onSectionNavigate?.('submit', currentSection, null, {
			sectionType: section.type,
			sectionName: section.name || section.id,
			totalSections: sections.length,
		}); } catch { /* no-op */ }
		setLoading(true);
		await onSubmit(values);
		setLoading(false);
	};

	const previous = (event) => {
		event.preventDefault();
		try { onSectionNavigate?.('previous', currentSection, currentSection - 1, {
			sectionType: section.type,
			sectionName: section.name || section.id,
			totalSections: sections.length,
		}); } catch { /* no-op */ }
		if (isBrowser) window.scrollTo({ top: 0, behavior: 'smooth' });
		onPrevious();
		setCurrentSection(Math.max(currentSection - 1, 0));
	};

	const {
		className = '',
		showSectionsHeader = false,
		navigation = {}
	} = schema;


	const navigationProps = {
		index: currentSection,
		navigation: section.navigation || navigation,
		sections,
		previous,
		next,
		submit,
		isLoading: loading,
		hasPrevious: currentSection > 0,
		hasNext: currentSection < sections.length - 1,
		canContinue: checkSectionComplete() && pendingActions.length === 0,
		customNavigationColors,
		disabled,
	};

	const clsNames = classNames('nf-form', className);

	return (
		<form className={clsNames} onSubmit={submit} style={style}>
			{showSectionsHeader &&
				<Tabs index={currentSection} sections={sections} onChange={setCurrentSection} />}
			<ErrorBoundary>
				<Section
					{...section}
					navigationProps={navigationProps}
					onChange={onChangeValues}
					commit={commitValues}
					values={values}
					errors={errors}
					schema={sections}
					addPendingAction={addPendingAction}
					CustomNavigation={CustomNavigation}
					removePendingAction={removePendingAction}
					selectedLanguage={selectedLanguage}
					disabled={disabled}
				/>
			</ErrorBoundary>
		</form>
	);
};


export default Form;
export const registry = Registry;
export const validatorRegistry = ValidatorRegistry;
